/**
 * 产品详情配置文件
 * 包含所有产品的名称、介绍、特点标签、价格版本等详细信息
 * 
 * 产品类型说明：
 * - aimonkey: AIMonkey&共享车队（共享AI服务平台）
 * - official: 官网登录账号&自助续费（原版官方账号服务）
 * - claudecode: Claude Code（Claude Code专业服务）
 */

import i18n from '../i18n';

export const productDetailConfig = {
  // AIMonkey&共享车队 产品配置
  aimonkey: {
    id: 'aimonkey',
    name: 'AIMonkey&共享车队',
    type: 'aimonkey',
    description: '基于云端AI车队的共享服务平台，包含ChatGPT、Claude、Sora、Grok等多种AI模型。通过我们的平台统一访问，享受高性价比的AI服务体验。',
    highlights: [
      '多AI模型整合',
      '共享车队服务',
      '高性价比',
      '统一平台访问',
      '对话隐私独立',
      '免费试用'
    ],
    aboutProduct: [
      'AIMonkey&共享车队是我们的核心产品，整合了市面上最优秀的AI模型服务。通过共享车队的方式，为用户提供高性价比的AI使用体验。'
    ],
    afterPurchase: [
      '获得AIMonkey平台访问权限',
      'ChatGPT、Claude、Sora、Grok等多AI模型使用权限',
      '个人对话记录独立存储',
      '专业客服技术支持'
    ],
    importantNotes: [
      '服务特性：基于共享车队模式，成本更低，性价比更高',
      '隐私保护：虽然是共享车队，但每个用户的对话记录完全独立',
      '使用限制：根据套餐有相应的使用频率限制',
      '质保服务：提供全程技术支持和服务保障'
    ],
    features: [
      { icon: '🤖', title: 'ChatGPT服务', desc: '4o模型3小时180次，o3模型每周40次' },
      { icon: '🧠', title: 'Claude服务', desc: i18n.t('fleetDesign.limits.claude') },
      { icon: '🎬', title: 'Sora服务', desc: 'AI视频生成，每小时20次调用' },
      { icon: '⚡', title: 'Grok服务', desc: '深度思考模型，每2小时50次调用' },
      { icon: '🔒', title: '隐私保护', desc: '个人对话记录独立存储，数据安全' },
      { icon: '💡', title: '统一平台', desc: '一个账号访问所有AI服务，简单便捷' }
    ],
    faq: [
      {
        question: '什么是AIMonkey&共享车队？',
        answer: '是我们的AI服务平台，通过共享车队的方式提供ChatGPT、Claude、Sora、Grok等多种AI模型服务，让用户以更低的成本享受高质量AI服务。'
      },
      {
        question: '共享车队是否安全？我的对话会被别人看到吗？',
        answer: '完全安全。虽然底层使用共享车队，但每个用户的对话记录都是独立存储的，其他用户无法看到您的对话内容。'
      },
      {
        question: '有使用次数限制吗？',
        answer: i18n.t('productDetail.common.faq.fleetUsageLimit')
      },
      {
        question: '可以免费试用吗？',
        answer: '可以联系客服申请免费试用账号，体验我们的服务质量。'
      },
      {
        question: '和购买官方账号有什么区别？',
        answer: '价格更优惠，一次购买可使用多种AI模型，但需要通过我们的平台访问，不是官方直接账号。'
      }
    ],
    versions: [
      {
        label: '基础版',
        month: 1,
        price: 9.9,
        originalPrice: 80,
        stock: 99,
        productId: 'aimonkey_basic_1m',
        key: 'basic',
        duration: '1个月'
      },
      {
        label: '标准版',
        month: 3,
        price: 26.7,
        originalPrice: 240,
        stock: 99,
        productId: 'aimonkey_standard_3m',
        key: 'standard',
        duration: '3个月'
      },
      {
        label: '专业版',
        month: 12,
        price: 89.9,
        originalPrice: 960,
        stock: 99,
        productId: 'aimonkey_pro_12m',
        key: 'professional',
        duration: '12个月'
      }
    ],
    specs: [
      { label: '访问方式', values: ['AIMonkey平台'] },
      { label: '产品类型', values: ['共享车队'] },
      { label: 'AI模型', values: ['ChatGPT', 'Claude', 'Sora', 'Grok'] },
      { label: '数据隐私', values: ['个人对话独立'] },
      { label: '技术支持', values: ['专业客服支持'] },
      { label: '使用限制', values: ['按频率限制'] },
      { label: '免费试用', values: ['支持申请试用'] }
    ]
  },

  // 官网登录账号&自助续费 产品配置
  official: {
    id: 'official',
    name: '官网登录账号&自助续费',
    type: 'official',
    description: '提供各大AI平台的原版官方账号，包含完整的登录信息，支持官网、APP等多端登录。账号独享使用，可自主管理密码和设置。',
    highlights: [
      '100%官方原版账号',
      '支持网页和APP登录',
      '账号完全独享',
      '可自主改密码',
      '多平台支持',
      '封号包赔'
    ],
    aboutProduct: [
      '直接提供各大AI平台的官方账号和密码，包括ChatGPT Plus、Claude Pro等。账号完全属于您个人使用，享受官方完整功能。'
    ],
    afterPurchase: [
      '获得完整的账号登录信息（邮箱+密码）',
      '支持修改密码和个人设置',
      '可在官网、手机APP等多端登录',
      '享受官方完整功能和服务',
      '专业售后和技术支持'
    ],
    importantNotes: [
      '账号性质：100%官方原版账号，非代理或共享',
      '使用权限：账号完全独享，可自主管理',
      '平台支持：支持官网、iOS/Android APP登录',
      '质保政策：30天质保期，遇到封号免费更换'
    ],
    features: [
      { icon: '🏢', title: '官方原版', desc: '100%来自官方的正版账号，功能完整' },
      { icon: '📱', title: '多端登录', desc: '支持网页、iOS、Android等多平台' },
      { icon: '🔐', title: '独享账号', desc: '账号完全属于您，可自主改密码' },
      { icon: '🛡️', title: '封号包赔', desc: '30天质保，遇封号免费提供新账号' },
      { icon: '⚡', title: '即买即用', desc: '购买后立即提供账号信息' },
      { icon: '🎯', title: '原版体验', desc: '享受官方完整功能，无任何限制' }
    ],
    faq: [
      {
        question: '这是真的官方账号吗？',
        answer: '是的，我们提供的都是100%官方正版账号，直接从官方渠道获得，享受完整的官方功能。'
      },
      {
        question: '我可以修改密码吗？',
        answer: '完全可以。我们会提供完整的邮箱和密码信息，您可以自主修改密码和管理账号设置。'
      },
      {
        question: '支持手机APP登录吗？',
        answer: '支持。您可以使用我们提供的账号信息在官方网站、iOS/Android APP等各个平台登录使用。'
      },
      {
        question: '账号会被封吗？如何处理？',
        answer: '我们有30天质保政策。如果在质保期内遇到账号被封的情况，我们会免费提供新的可用账号。'
      },
      {
        question: '和共享车队有什么区别？',
        answer: '这是完全独享的官方账号，只有您一个人使用，可以自主管理，享受官方完整体验，价格相对较高。'
      }
    ],
    versions: [
      {
        label: 'ChatGPT Plus账号',
        month: 1,
        price: 19.9,
        originalPrice: 20,
        stock: 10,
        productId: 'official_chatgpt_1m',
        key: 'chatgpt',
        duration: '1个月'
      },
      {
        label: 'Claude Pro账号',
        month: 1,
        price: 19.9,
        originalPrice: 20,
        stock: 8,
        productId: 'official_claude_1m',
        key: 'claude',
        duration: '1个月'
      },
      {
        label: 'ChatGPT Plus年费',
        month: 12,
        price: 199.9,
        originalPrice: 240,
        stock: 5,
        productId: 'official_chatgpt_12m',
        key: 'chatgpt_yearly',
        duration: '12个月'
      }
    ],
    specs: [
      { label: '账号类型', values: ['官方原版账号'] },
      { label: '使用方式', values: ['独享使用'] },
      { label: '登录平台', values: ['官网', 'iOS APP', 'Android APP'] },
      { label: '密码管理', values: ['可自主修改'] },
      { label: '功能权限', values: ['官方完整功能'] },
      { label: '质保政策', values: ['30天封号包赔'] },
      { label: '交付方式', values: ['邮箱+密码'] }
    ]
  },

  // Claude Code 产品配置
  claudecode: {
    id: 'claudecode',
    name: 'Claude Code',
    type: 'claudecode',
    description: 'Anthropic最新推出的Claude Code服务，专为开发者设计的AI编程助手。提供代码生成、调试、重构等强大功能，是程序员的得力助手。',
    highlights: [
      'AI编程助手',
      '代码生成优化',
      '支持多种编程语言',
      '智能调试建议',
      '项目级理解',
      '实时代码分析'
    ],
    aboutProduct: [
      'Claude Code是Anthropic专为开发者推出的AI编程服务，具备强大的代码理解、生成和优化能力。支持主流编程语言，可以协助完成各种开发任务。'
    ],
    afterPurchase: [
      '获得Claude Code服务访问权限',
      '支持VS Code等主流IDE集成',
      '无限制的代码生成和分析',
      '专业的编程问题解答',
      '项目级代码理解能力'
    ],
    importantNotes: [
      '服务特色：专门针对编程场景优化的AI模型',
      '使用方式：可通过网页端、IDE插件等方式访问',
      '语言支持：支持Python、JavaScript、Java、C++等主流语言',
      '项目支持：可理解整个项目结构，提供更准确的建议'
    ],
    features: [
      { icon: '💻', title: '智能编程', desc: '强大的代码生成和补全能力' },
      { icon: '🔍', title: '代码分析', desc: '深入理解代码逻辑，提供优化建议' },
      { icon: '🛠️', title: '调试助手', desc: '协助发现和修复代码问题' },
      { icon: '📚', title: '多语言支持', desc: '支持主流编程语言和框架' },
      { icon: '🔗', title: 'IDE集成', desc: '无缝集成VS Code等开发环境' },
      { icon: '⚡', title: '实时响应', desc: '快速生成代码和解决方案' }
    ],
    faq: [
      {
        question: '什么是Claude Code？',
        answer: 'Claude Code是Anthropic最新推出的AI编程助手，专门为开发者设计，提供代码生成、分析、调试等功能。'
      },
      {
        question: '支持哪些编程语言？',
        answer: '支持Python、JavaScript、TypeScript、Java、C++、C#、Go、Rust等主流编程语言。'
      },
      {
        question: '如何在VS Code中使用？',
        answer: '可以安装Claude Code的VS Code插件，然后使用我们提供的账号信息登录使用。'
      },
      {
        question: '能理解整个项目吗？',
        answer: '是的，Claude Code具备项目级理解能力，可以分析项目结构，提供更准确的代码建议。'
      },
      {
        question: '和普通的Claude有什么区别？',
        answer: 'Claude Code专门针对编程场景优化，在代码理解、生成和调试方面表现更出色。'
      }
    ],
    versions: [
      {
        label: '个人版',
        month: 1,
        price: 29.9,
        originalPrice: 50,
        stock: 20,
        productId: 'claudecode_personal_1m',
        key: 'personal',
        duration: '1个月'
      },
      {
        label: '专业版',
        month: 3,
        price: 79.9,
        originalPrice: 150,
        stock: 15,
        productId: 'claudecode_pro_3m',
        key: 'professional',
        duration: '3个月'
      },
      {
        label: '企业版',
        month: 12,
        price: 299.9,
        originalPrice: 600,
        stock: 10,
        productId: 'claudecode_enterprise_12m',
        key: 'enterprise',
        duration: '12个月'
      }
    ],
    specs: [
      { label: '服务类型', values: ['AI编程助手'] },
      { label: '使用方式', values: ['网页端', 'IDE插件'] },
      { label: '编程语言', values: ['Python', 'JavaScript', 'Java', 'C++', '等'] },
      { label: '核心功能', values: ['代码生成', '调试分析', '项目理解'] },
      { label: '集成支持', values: ['VS Code', 'IntelliJ', '等主流IDE'] },
      { label: '响应速度', values: ['实时响应'] },
      { label: '项目支持', values: ['项目级理解'] }
    ]
  },

  // 钻石 产品配置
  diamond: {
    id: 'diamond',
    name: '钻石',
    type: 'diamond',
    description: '最高端的AI服务套餐，包含所有AI模型的高级权限和专属功能。享受无限制使用、优先支持和独家功能，为追求极致体验的用户打造。',
    highlights: [
      '所有AI模型无限制',
      '专属客户经理',
      '优先技术支持',
      '独家高级功能',
      '定制化服务',
      '企业级支持'
    ],
    aboutProduct: [
      '钻石套餐是我们的旗舰产品，为最高端用户提供无与伦比的AI服务体验。包含所有AI模型的完整访问权限，无使用限制，配备专属客户经理和技术支持团队。'
    ],
    afterPurchase: [
      '获得所有AI平台的完整访问权限',
      '配备专属客户经理一对一服务',
      '享受优先技术支持和问题解决',
      '获得独家高级功能和提前体验权',
      '定制化服务方案和企业级支持'
    ],
    importantNotes: [
      '服务级别：最高端服务，无任何使用限制',
      '支持团队：专属客户经理和技术支持团队',
      '功能权限：所有AI模型的完整功能权限',
      '定制服务：可根据需求提供定制化解决方案'
    ],
    features: [
      { icon: '💎', title: '钻石级服务', desc: '最高端的AI服务体验，无任何限制' },
      { icon: '👨‍💼', title: '专属客户经理', desc: '一对一专属服务，24/7随时响应' },
      { icon: '🚀', title: '无限制使用', desc: '所有AI模型无使用频率和次数限制' },
      { icon: '🔮', title: '独家功能', desc: '率先体验最新功能和模型' },
      { icon: '🏢', title: '企业级支持', desc: '企业级技术支持和定制化服务' },
      { icon: '⚡', title: '优先响应', desc: '最高优先级的技术支持和问题解决' }
    ],
    faq: [
      {
        question: '钻石套餐包含哪些服务？',
        answer: '包含所有AI模型（ChatGPT、Claude、Sora、Grok等）的完整访问权限，无使用限制，配备专属客户经理和企业级技术支持。'
      },
      {
        question: '和其他套餐有什么区别？',
        answer: '钻石套餐提供最高端的服务体验，无任何使用限制，配备专属服务团队，享受独家功能和定制化服务。'
      },
      {
        question: '是否有使用限制？',
        answer: '完全没有使用限制。您可以无限制使用所有AI模型，享受最高级别的服务体验。'
      },
      {
        question: '专属客户经理提供什么服务？',
        answer: '专属客户经理提供一对一服务，包括产品咨询、技术支持、定制化需求处理和优先问题解决。'
      },
      {
        question: '是否支持企业用户？',
        answer: '完全支持企业用户，提供企业级技术支持、定制化解决方案和团队协作功能。'
      }
    ],
    versions: [
      {
        label: '钻石月度',
        month: 1,
        price: 199.9,
        originalPrice: 500,
        stock: 5,
        productId: 'diamond_monthly_1m',
        key: 'monthly',
        duration: '1个月'
      },
      {
        label: '钻石季度',
        month: 3,
        price: 499.9,
        originalPrice: 1500,
        stock: 3,
        productId: 'diamond_quarterly_3m',
        key: 'quarterly',
        duration: '3个月'
      },
      {
        label: '钻石年度',
        month: 12,
        price: 1599.9,
        originalPrice: 6000,
        stock: 2,
        productId: 'diamond_yearly_12m',
        key: 'yearly',
        duration: '12个月'
      }
    ],
    specs: [
      { label: '服务级别', values: ['钻石级'] },
      { label: '使用限制', values: ['无限制'] },
      { label: 'AI模型', values: ['全部模型完整权限'] },
      { label: '客户服务', values: ['专属客户经理'] },
      { label: '技术支持', values: ['企业级24/7支持'] },
      { label: '独家功能', values: ['提前体验新功能'] },
      { label: '定制服务', values: ['完全定制化'] }
    ]
  }
};

/**
 * 根据产品类型和ID获取产品详情
 * @param {string} type - 产品类型
 * @param {string} id - 产品ID（可选）
 * @returns {object} 产品详情对象
 */
export const getProductDetail = (type, id) => {
  // 如果没有找到对应的type，返回默认配置
  if (!productDetailConfig[type]) {
    console.warn(`未找到产品类型: ${type}，返回默认配置`);
    return {
      id: 'default',
      name: 'AI服务',
      description: '优质的AI服务，提升您的工作效率',
      highlights: ['高品质服务', '快速交付', '专业支持', '售后保障'],
      features: [
        { icon: '🎯', title: '专业服务', desc: '提供专业级别的服务体验' },
        { icon: '🚀', title: '快速响应', desc: '1小时内快速处理和响应' },
        { icon: '🔒', title: '安全可靠', desc: '100%原版服务，品质保证' },
        { icon: '💎', title: '优质体验', desc: '个人专属，无共享问题' }
      ],
      versions: [
        {
          label: '基础版',
          month: 1,
          price: 14.9,
          originalPrice: 29.9,
          stock: 99,
          productId: 'default_basic',
          key: 'basic'
        }
      ],
      specs: [
        { label: '服务类型', values: ['基础版'] },
        { label: '使用平台', values: ['Web浏览器'] },
        { label: '隐私保护', values: ['个人专属'] },
        { label: '退款政策', values: ['支持退款'] }
      ]
    };
  }

  return productDetailConfig[type];
};

/**
 * 获取产品的价格版本信息
 * @param {string} type - 产品类型
 * @returns {array} 价格版本数组
 */
export const getProductVersions = (type) => {
  const product = getProductDetail(type);
  return product.versions || [];
};

/**
 * 获取产品的特点标签
 * @param {string} type - 产品类型
 * @returns {array} 特点标签数组
 */
export const getProductHighlights = (type) => {
  const product = getProductDetail(type);
  return product.highlights || [];
};

/**
 * 获取产品的功能特性
 * @param {string} type - 产品类型
 * @returns {array} 功能特性数组
 */
export const getProductFeatures = (type) => {
  const product = getProductDetail(type);
  return product.features || [];
};

/**
 * 获取产品的技术规格
 * @param {string} type - 产品类型
 * @returns {array} 技术规格数组
 */
export const getProductSpecs = (type) => {
  const product = getProductDetail(type);
  return product.specs || [];
};

/**
 * 获取产品的FAQ（购买须知）
 * @param {string} type - 产品类型
 * @returns {array} FAQ数组
 */
export const getProductFAQ = (type) => {
  const product = getProductDetail(type);
  return product.faq || [];
};